import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, Link2, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lien de reprise d'un projet : à coller dans Codex, Orca, claude.ai… L'outil y lit
 * mémoire, CDC, tâches et fil des échanges, et y consigne son travail.
 */
export function promptsReprise(nom, lien) {
  const journal = (source) =>
    `curl -s -X POST ${lien.journal} -H 'Content-Type: application/json' -d '{"source":"${source}","titre":"Objet de la session","type":"fin","texte":"Ce qui a été fait, décidé, et ce qui reste"}'`;
  const base = (source) =>
    `Tu reprends le projet NovaForge « ${nom} ».\n` +
    `1. Lis d'abord tout son contexte (mémoire, règles, CDC, tâches, derniers échanges) : curl -s ${lien.url}\n` +
    `2. Ne me redemande rien de ce qui y figure.\n` +
    `3. À la fin de la session, consigne ton travail pour que le fil continue dans NovaForge :\n${journal(source)}`;
  return [
    { cle: "orca", libelle: "Claude Code / Orca", texte: base("orca") },
    { cle: "codex", libelle: "Codex", texte: base("codex") },
    { cle: "web", libelle: "claude.ai / ChatGPT", texte: `Tu reprends le projet NovaForge « ${nom} ». Lis son contexte complet à cette adresse avant de répondre : ${lien.url}` },
  ];
}

export default function ReprisePanneau({ projetId, nom, token }) {
  const [lien, setLien] = useState(null);
  const h = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API_URL}/api/agent/projets/${projetId}/reprise`, { headers: h })
      .then((r) => (r.ok ? r.json() : null)).then(setLien).catch(() => {});
    // eslint-disable-next-line
  }, [projetId, token]);

  const copier = (texte, quoi) => {
    navigator.clipboard.writeText(texte).then(() => toast.success(`${quoi} copié`));
  };
  const regenerer = async () => {
    const r = await fetch(`${API_URL}/api/agent/projets/${projetId}/reprise/regenerer`, { method: "POST", headers: h });
    if (r.ok) { setLien(await r.json()); toast.success("Nouveau lien : l'ancien ne fonctionne plus"); }
  };

  if (!lien) return null;
  return (
    <section className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
      <h2 className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-violet-700">
        <Link2 className="h-4 w-4" /> Reprendre ce projet ailleurs
      </h2>
      <p className="mt-1 text-xs text-slate-600">
        Colle le texte dans Codex, Orca ou un autre outil : il récupère la mémoire, le CDC, les tâches et le fil
        des échanges, puis consigne son travail ici. Ce lien donne accès au contexte du projet : ne le partage
        qu'avec tes outils.
      </p>
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5">
        <code className="min-w-0 flex-1 truncate text-xs text-slate-700">{lien.url}</code>
        <button onClick={() => copier(lien.url, "Lien")} className="rounded p-1 hover:bg-slate-100" title="Copier le lien"><Copy className="h-4 w-4" /></button>
        <a href={lien.url} target="_blank" rel="noreferrer" className="rounded p-1 hover:bg-slate-100" title="Voir ce que lit l'outil"><ExternalLink className="h-4 w-4" /></a>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {promptsReprise(nom, lien).map((p) => (
          <button key={p.cle} onClick={() => copier(p.texte, `Texte pour ${p.libelle}`)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
            <Copy className="h-3.5 w-3.5" /> {p.libelle}
          </button>
        ))}
        <button onClick={regenerer} className="ml-auto inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs text-slate-600 hover:bg-white">
          <RefreshCw className="h-3.5 w-3.5" /> Nouveau lien
        </button>
      </div>
    </section>
  );
}
